import { useId, useState, type ChangeEvent } from "react";
import { examples } from "../domain/examples";
import type { AnswerValue, Question } from "../domain/types";

type Props = {
  question: Question;
  value: AnswerValue | undefined;
  error?: string;
  onChange: (value: AnswerValue) => void;
};

function countFor(value: string, unit: "characters" | "words"): number {
  if (unit === "characters") return value.length;
  return value.trim() ? value.trim().split(/\s+/).length : 0;
}

export function AnswerField({ question, value, error, onChange }: Props) {
  const id = useId();
  const [exampleOpen, setExampleOpen] = useState(false);
  const stringValue = typeof value === "string" ? value : "";
  const selected = Array.isArray(value) ? value : [];
  const guide = question.guidance;
  const count = guide ? countFor(stringValue, guide.unit) : 0;
  const outsideGuide = Boolean(
    guide &&
      stringValue &&
      ((guide.recommendedMin !== undefined && count < guide.recommendedMin) ||
        (guide.recommendedMax !== undefined && count > guide.recommendedMax)),
  );
  const example = question.exampleId ? examples[question.exampleId] : undefined;

  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy = [guide ? helpId : "", error ? errorId : ""].filter(Boolean).join(" ") || undefined;

  if (question.answerType === "choice") {
    return (
      <fieldset className="answer-field answer-choice" aria-describedby={describedBy}>
        <legend className="sr-only">{question.label}</legend>
        {question.options?.map((item) => {
          const checked = stringValue === item.value;
          return (
            <label className="choice-row" data-selected={checked} key={item.value}>
              <input
                type="radio"
                name={question.id}
                value={item.value}
                checked={checked}
                onChange={() => onChange(item.value)}
              />
              <span className="choice-mark" aria-hidden="true" />
              <span>
                <strong>{item.label}</strong>
                {item.detail ? <small>{item.detail}</small> : null}
              </span>
            </label>
          );
        })}
        {error ? <p className="field-error" id={errorId} role="alert">{error}</p> : null}
      </fieldset>
    );
  }

  if (question.answerType === "multi") {
    return (
      <fieldset className="answer-field answer-choice" aria-describedby={describedBy}>
        <legend className="sr-only">{question.label}</legend>
        {question.options?.map((item) => {
          const checked = selected.includes(item.value);
          return (
            <label className="choice-row" data-selected={checked} key={item.value}>
              <input
                type="checkbox"
                name={question.id}
                value={item.value}
                checked={checked}
                onChange={() => {
                  const next = checked ? selected.filter((entry) => entry !== item.value) : [...selected, item.value];
                  onChange(next);
                }}
              />
              <span className="choice-mark choice-mark-square" aria-hidden="true" />
              <span>
                <strong>{item.label}</strong>
                {item.detail ? <small>{item.detail}</small> : null}
              </span>
            </label>
          );
        })}
        {error ? <p className="field-error" id={errorId} role="alert">{error}</p> : null}
      </fieldset>
    );
  }

  const inputProps = {
    id,
    value: stringValue === "unknown" ? "" : stringValue,
    placeholder: question.placeholder,
    maxLength: guide?.hardMaxCharacters ?? 12_000,
    "aria-describedby": describedBy,
    "aria-invalid": Boolean(error),
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(event.target.value),
  };

  return (
    <div className="answer-field">
      {question.answerType === "long" ? (
        <textarea {...inputProps} rows={8} />
      ) : (
        <input
          {...inputProps}
          type={question.answerType === "date" ? "date" : question.answerType === "url" ? "url" : "text"}
        />
      )}

      <div className="field-meta">
        {guide ? (
          <p className={outsideGuide ? "guidance outside" : "guidance"} id={helpId}>
            {guide.note} {stringValue ? <span>{count.toLocaleString()} {guide.unit}.</span> : null}
          </p>
        ) : <span />}
        {question.uncertaintyAllowed ? (
          <button className="text-button" type="button" onClick={() => onChange("unknown")}>
            I don’t know yet
          </button>
        ) : null}
      </div>

      {stringValue === "unknown" ? (
        <div className="unknown-state" role="status">
          Kept as unknown. That is better than a confident guess.
          <button type="button" className="text-button" onClick={() => onChange("")}>Answer instead</button>
        </div>
      ) : null}

      {error ? <p className="field-error" id={errorId} role="alert">{error}</p> : null}

      {example ? (
        <div className="example-disclosure">
          <button
            type="button"
            className="example-trigger"
            aria-expanded={exampleOpen}
            onClick={() => setExampleOpen((open) => !open)}
          >
            <span>{exampleOpen ? "Close example" : "See a strong example"}</span>
            <span aria-hidden="true">{exampleOpen ? "−" : "+"}</span>
          </button>
          {exampleOpen ? (
            <div className="example-sheet">
              <p className="mono-label">WHAT THIS ANSWER NEEDS TO DO</p>
              <p>{example.purpose}</p>
              <blockquote>{example.example}</blockquote>
              <p><strong>Why it works.</strong> {example.whyItWorks}</p>
              <p className="example-warning"><strong>Keep your own voice.</strong> {example.doNotCopy}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
